import { useState, useMemo, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Lock, ArrowLeft, ShieldCheck, AlertTriangle } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import { TurnstileWidget, withTurnstile } from "../components/TurnstileWidget";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ResetPassword() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");

  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(null);
  const [turnstileToken, setTurnstileToken] = useState("");
  const [turnstileResetKey, setTurnstileResetKey] = useState(0);

  const resetTurnstile = useCallback(() => {
    setTurnstileToken("");
    setTurnstileResetKey(k => k + 1);
  }, []);

  const pwRequirements = useMemo(() => {
    if (!newPassword) return [];
    return [
      { label: "10+ characters", check: newPassword.length >= 10 },
      { label: "Uppercase letter", check: /[A-Z]/.test(newPassword) },
      { label: "Lowercase letter", check: /[a-z]/.test(newPassword) },
      { label: "Number", check: /[0-9]/.test(newPassword) },
      { label: "Special character", check: /[^A-Za-z0-9]/.test(newPassword) },
      { label: "Passwords match", check: newPassword === confirmPassword && confirmPassword.length > 0 },
    ];
  }, [newPassword, confirmPassword]);

  const allMet = pwRequirements.length > 0 && pwRequirements.every(r => r.check);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (newPassword !== confirmPassword) {
      toast.error("Passwords don't match");
      return;
    }
    if (!turnstileToken) {
      toast.error("Please complete the CAPTCHA challenge");
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const res = await axios.post(`${API}/auth/reset-password`, {
        token,
        new_password: newPassword,
      }, withTurnstile(turnstileToken));
      toast.success(res.data.message || "Password reset successful!");
      setSuccess(true);
    } catch (err) {
      const detail = err.response?.data?.detail || "Failed to reset password. The link may have expired.";
      setError(detail);
      toast.error(detail);
      resetTurnstile();
    } finally {
      setLoading(false);
    }
  };

  // No token in URL
  if (!token) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-background to-muted/30 px-4">
        <div className="w-full max-w-md space-y-4">
          <Card>
            <CardHeader className="pb-3 text-center">
              <AlertTriangle className="w-10 h-10 text-amber-500 mx-auto mb-2" />
              <CardTitle className="text-lg">Invalid Reset Link</CardTitle>
              <CardDescription>This password reset link is missing or malformed.</CardDescription>
            </CardHeader>
            <CardContent>
              <Button className="w-full" onClick={() => navigate("/login")}>
                <ArrowLeft className="w-4 h-4 mr-2" /> Back to Login
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // Success state
  if (success) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-background to-muted/30 px-4">
        <div className="w-full max-w-md space-y-4">
          <div className="text-center space-y-2">
            <h1 className="text-xl font-bold tracking-tight">SpillConsult Pulse</h1>
            <p className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">Secured Business Intelligence</p>
          </div>
          <Card>
            <CardHeader className="pb-3 text-center">
              <ShieldCheck className="w-10 h-10 text-emerald-500 mx-auto mb-2" />
              <CardTitle className="text-lg">Password Reset Complete</CardTitle>
              <CardDescription>Your password has been successfully updated.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              <p className="text-sm text-muted-foreground text-center">
                You can now sign in with your new password. You will be prompted to set up 2FA if it was previously configured.
              </p>
              <Button className="w-full" onClick={() => navigate("/login")}>
                Sign In
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // Reset form
  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-background to-muted/30 px-4">
      <div className="w-full max-w-md space-y-4">
        <div className="text-center space-y-2">
          <h1 className="text-xl font-bold tracking-tight">SpillConsult Pulse</h1>
          <p className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">Secured Business Intelligence</p>
        </div>

        <Card>
          <CardHeader className="pb-3">
            <div className="flex items-center gap-2">
              <Lock className="w-5 h-5 text-primary" />
              <div>
                <CardTitle className="text-lg">Set New Password</CardTitle>
                <CardDescription>Choose a strong password for your account</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {error && (
              <div className="mb-4 rounded-lg bg-destructive/10 border border-destructive/20 p-3 text-sm text-destructive" data-testid="reset-error">
                {error}
              </div>
            )}
            <form onSubmit={handleSubmit} className="space-y-3">
              <div className="space-y-1.5">
                <Label className="text-xs">New Password</Label>
                <Input
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  required
                  minLength={10}
                  placeholder="Minimum 10 characters"
                  data-testid="reset-new-password"
                />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Confirm Password</Label>
                <Input
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                  placeholder="Re-enter your password"
                  data-testid="reset-confirm-password"
                />
              </div>

              {pwRequirements.length > 0 && (
                <div className="grid grid-cols-2 gap-1">
                  {pwRequirements.map((r, i) => (
                    <span key={i} className={`text-[10px] ${r.check ? "text-emerald-600" : "text-muted-foreground"}`}>
                      {r.check ? "✓" : "✗"} {r.label}
                    </span>
                  ))}
                </div>
              )}

              <TurnstileWidget onToken={setTurnstileToken} onExpired={() => setTurnstileToken("")} resetKey={turnstileResetKey} />

              <Button type="submit" className="w-full" disabled={loading || !allMet || !turnstileToken} data-testid="reset-submit-btn">
                {loading ? "Resetting..." : "Reset Password"}
              </Button>
            </form>

            <div className="mt-4 text-center">
              <button
                type="button"
                onClick={() => navigate("/login")}
                className="text-xs text-muted-foreground hover:text-foreground hover:underline"
              >
                Back to Login
              </button>
            </div>
          </CardContent>
        </Card>

        <p className="text-center text-[10px] text-muted-foreground">
          Protected by brute-force detection, 2FA, and encrypted sessions
        </p>
      </div>
    </div>
  );
}
